const LARGE_IMAGE_LONGEST_SIDE = 2000;
const LARGE_IMAGE_QUALITY = 100;
const MAX_DEVICE_PIXEL_RATIO = 3;
const DEFAULT_FIT_MODE = 'max';

const getDevicePixelRatio = () => {
    if (typeof window === 'undefined' || !window.devicePixelRatio) return 1;
    return Math.min(window.devicePixelRatio, MAX_DEVICE_PIXEL_RATIO);
};

const getViewportSize = () => {
    if (typeof window === 'undefined') {
        return {
            width: LARGE_IMAGE_LONGEST_SIDE,
            height: LARGE_IMAGE_LONGEST_SIDE,
        };
    }

    return {
        width: window.innerWidth || LARGE_IMAGE_LONGEST_SIDE,
        height: window.innerHeight || LARGE_IMAGE_LONGEST_SIDE,
    };
};

const getSourceConfig = (source) => {
    const fileUrl = typeof source === 'string'
        ? source
        : source?.fields?.file?.url;

    if (!fileUrl) {
        return {
            url: '',
            originalWidth: undefined,
            originalHeight: undefined,
            existingQuery: '',
        };
    }

    const [baseUrl, existingQuery = ''] = fileUrl.split('?');

    return {
        url: baseUrl.startsWith('http') ? baseUrl : `https:${baseUrl}`,
        originalWidth: typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.width,
        originalHeight: typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.height,
        existingQuery,
    };
};

const clampDimension = (value, max) => {
    if (!value) return undefined;

    const rounded = Math.ceil(value);
    return max ? Math.min(rounded, max) : rounded;
};

const buildSanityImageUrl = (source, options = {}) => {
    const { url, originalWidth, originalHeight, existingQuery } = getSourceConfig(source);
    if (!url) return '';

    const params = new URLSearchParams(existingQuery);
    const width = clampDimension(options.width, originalWidth);
    const height = clampDimension(options.height, originalHeight);

    if (width) params.set('w', String(width));
    else params.delete('w');

    if (height) params.set('h', String(height));
    else params.delete('h');

    if (options.quality) params.set('q', String(options.quality));
    else params.delete('q');

    params.set('fit', options.fit || DEFAULT_FIT_MODE);

    if (!options.format || options.format === 'auto') {
        params.delete('fm');
        params.set('auto', 'format');
    } else {
        params.delete('auto');
        params.set('fm', options.format);
    }

    return `${url}?${params.toString()}`;
};

const getResponsiveDimensionForSource = (source, requestedLongestSide) => {
    const { originalWidth, originalHeight } = getSourceConfig(source);

    if (originalWidth && originalHeight) {
        return originalHeight > originalWidth
            ? { height: requestedLongestSide }
            : { width: requestedLongestSide };
    }

    return { width: requestedLongestSide };
};

export const getOptimizedImageUrl = (image, options = {}) => {
    const dpr = getDevicePixelRatio();
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const config = isMobile ? (options.mobile || {}) : (options.desktop || {});

    return buildSanityImageUrl(image, {
        width: config.width ? config.width * dpr : undefined,
        height: config.height ? config.height * dpr : undefined,
        quality: config.quality,
        fit: config.fit || DEFAULT_FIT_MODE,
        format: options.format,
    });
};

export const getThumbnailUrl = (image, options = {}) => {
    // Get original dimensions to determine orientation
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    let thumbnailConfig;

    if (originalWidth && originalHeight) {
        const isPortrait = originalHeight > originalWidth;
        const isLandscape = originalWidth > originalHeight;

        if (isPortrait) {
            // For portraits: constrain by height
            thumbnailConfig = {
                mobile: {
                    height: 200,
                    quality: 85,
                    fit: 'fill'
                },
                desktop: {
                    height: 400,
                    quality: 85,
                    fit: 'fill'
                }
            };
        } else if (isLandscape) {
            // For landscapes: constrain by width
            thumbnailConfig = {
                mobile: {
                    width: 200,
                    quality: 85,
                    fit: 'fill'
                },
                desktop: {
                    width: 400,
                    quality: 85,
                    fit: 'fill'
                }
            };
        } else {
            // For square images: use height constraint
            thumbnailConfig = {
                mobile: {
                    height: 200,
                    quality: 85,
                    fit: 'fill'
                },
                desktop: {
                    height: 400,
                    quality: 85,
                    fit: 'fill'
                }
            };
        }
    } else {
        // Fallback to original config
        thumbnailConfig = {
            mobile: {
                height: 200,
                quality: 85,
                fit: 'fill'
            },
            desktop: {
                height: 400,
                quality: 85,
                fit: 'fill'
            }
        };
    }

    return getOptimizedImageUrl(image, { ...thumbnailConfig, ...options });
};

// Returns a DPR-aware optimized URL for either a CMS asset object or a direct URL string.
export const getDprImageUrl = (source, baseMax = 750, opts = {}) => {
    const dpr = getDevicePixelRatio();
    const maxPx = Math.ceil(baseMax * dpr);

    return buildSanityImageUrl(source, {
        ...getResponsiveDimensionForSource(source, maxPx),
        quality: opts.quality || 85,
        fit: opts.fit || DEFAULT_FIT_MODE,
        format: opts.format,
    });
};

export function getLargeImageUrl(source, opts = {}) {
    const dpr = getDevicePixelRatio();
    const viewport = getViewportSize();
    const requestedLongestSide = Math.min(
        Math.ceil(Math.max(viewport.width, viewport.height) * dpr),
        LARGE_IMAGE_LONGEST_SIDE,
    );

    return buildSanityImageUrl(source, {
        ...getResponsiveDimensionForSource(source, requestedLongestSide),
        quality: opts.quality || LARGE_IMAGE_QUALITY,
        fit: opts.fit || DEFAULT_FIT_MODE,
        format: opts.format,
    });
}

export function getSmallImageUrl(source, opts = {}) {
    const dpr = getDevicePixelRatio();
    const baseLongestSide = 300;
    const maxPx = Math.ceil(baseLongestSide * dpr);

    return buildSanityImageUrl(source, {
        ...getResponsiveDimensionForSource(source, maxPx),
        quality: opts.quality || 35,
        fit: opts.fit || DEFAULT_FIT_MODE,
        format: opts.format,
    });
}

